import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { trackEvent } from '@/lib/analytics/ga';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { RelatedNewsRow } from '@/components/news/RelatedNewsRow';
import { Seo } from '@/components/seo/Seo';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  Car,
  Zap,
  Fuel,
  Battery,
  Gauge,
  Timer,
  Weight,
  Package,
  Users,
  DoorOpen,
  ExternalLink,
  Video,
  FileText,
  Calendar,
  Globe,
  ChevronRight,
  BatteryCharging,
  Plug,
  MapPin,
  GitCompareArrows,
  Check,
  Clock,
  AlertCircle,
  Baby,
  Layers,
} from 'lucide-react';
import { formatPrice, formatNumber, formatDate } from '@/lib/formatters';
import {
  powertrainLabels,
  drivetrainLabels,
  transmissionLabels,
  bodyTypeLabels,
  priceTypeLabels,
  reviewFormatLabels,
  reviewLanguageLabels,
  getPowertrainBadgeVariant,
} from '@/types/database';
import { useCompare } from '@/contexts/CompareContext';
import type { Tables } from '@/integrations/supabase/types';
import { SimilarCars } from '@/components/cars/SimilarCars';
import { ModelVariants } from '@/components/cars/ModelVariants';
import { ModelCommunityCard } from '@/components/community/ModelCommunityCard';
import { VariantCommunitySection } from '@/components/cars/VariantCommunitySection';
import { RelatedArticles } from '@/components/cars/RelatedArticles';
import { FollowUsCard } from '@/components/marketing/FollowUsCard';

import { ReportErrorButton } from '@/components/cars/ReportErrorButton';
import { SuggestReviewButton } from '@/components/cars/SuggestReviewButton';
import { publisherToSlug } from '@/lib/publisherSlug';
import { toast } from 'sonner';


import { PriceHistorySection } from '@/components/cars/PriceHistorySection';
import { VariantVoteBox } from '@/components/cars/VariantVoteBox';
import { VariantReasonsVote } from '@/components/cars/VariantReasonsVote';
import { VariantCompetitorVote } from '@/components/cars/VariantCompetitorVote';

import { DealerLinksSection } from '@/components/cars/DealerLinksSection';
import { CarDetailAffiliateBlock } from '@/components/affiliate/CarDetailAffiliateBlock';
import { MissingFieldsBox } from '@/components/cars/MissingFieldsBox';
import { EquipmentBox } from '@/components/cars/EquipmentBox';
import { MultiVariantSuggestDialog } from '@/components/cars/MultiVariantSuggestDialog';
import { SuggestImageDialog } from '@/components/cars/SuggestImageDialog';
import { ContributeButton } from '@/components/cars/ContributeButton';
import { getFlowFields, type FlowKind } from '@/constants/contribute-flows';
import { TrendingUp } from 'lucide-react';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ShareButton } from '@/components/share/ShareButton';
import { applySiblingFallback, type Sibling, type BorrowedField } from '@/lib/variantSpecFallback';
import { KeyFactsBar } from '@/components/cars/KeyFactsBar';
import { DataTrustFooter } from '@/components/cars/DataTrustFooter';
import { CarCalculators } from '@/components/cars/CarCalculators';
import { UnverifiedBadge } from '@/components/cars/UnverifiedBadge';
import { OwnershipCostBlock } from '@/components/cars/OwnershipCostBlock';
import { SourceAttribution } from '@/components/cars/SourceAttribution';
import { useDataPointMeta, DataPointProvider } from '@/hooks/useDataPointMeta';
import { FavoriteButton } from '@/components/cars/FavoriteButton';
import { PriceAlertButton } from '@/components/cars/PriceAlertButton';

import { buildCarImageUrl, buildCarImageSrcSet } from '@/lib/car-image';

type Variant = Tables<'variants'>;
type Model = Tables<'models'>;
type Brand = Tables<'brands'>;
type PowertrainSpecs = Tables<'powertrain_specs'>;
type PriceDK = Tables<'prices_dk'>;
type PriceHistory = Tables<'price_history'>;
type ReviewLink = Tables<'review_links'>;

interface PriceWithHistory extends PriceDK {
  price_history: PriceHistory[];
}

interface PreviousGenLink {
  id: string;
  name: string;
  slug: string;
  model_year: number | null;
  brand_slug: string;
  first_variant_slug: string | null;
}

interface CarDetailData {
  variant: Variant;
  model: Model;
  brand: Brand;
  specs: PowertrainSpecs | null;
  prices: PriceWithHistory[];
  reviews: ReviewLink[];
  previousGenerations: PreviousGenLink[];
  activeGeneration: PreviousGenLink | null;
  siblings: Sibling[];
}

export default function CarDetail() {
  const { brandSlug, modelSlug, variantSlug } = useParams<{
    brandSlug: string;
    modelSlug: string;
    variantSlug: string;
  }>();

  // ALL hooks must run on every render — keep them above any early return
  const { addCar, removeCar, isSelected, selectedCars, maxCars } = useCompare();
  const [suggestFlow, setSuggestFlow] = useState<{ kind: FlowKind; fieldKey?: string } | null>(null);
  const [moreActionsOpen, setMoreActionsOpen] = useState(false);


  const { data, isLoading, error } = useQuery({
    queryKey: ['car-detail', brandSlug, modelSlug, variantSlug],
    queryFn: async (): Promise<CarDetailData | null> => {
      // Fetch variant with model and brand
      const { data: variants, error: variantError } = await supabase
        .from('variants')
        .select(`
          *,
          models!inner (
            *,
            brands!inner (*)
          )
        `)
        .eq('slug', variantSlug!)
        .eq('models.slug', modelSlug!)
        .eq('models.brands.slug', brandSlug!);

      if (variantError) throw variantError;
      if (!variants || variants.length === 0) return null;

      const variantData = variants[0];
      const model = variantData.models as unknown as Model & { brands: Brand };
      const brand = model.brands;

      // Fetch specs
      const { data: specs } = await supabase
        .from('powertrain_specs')
        .select('*')
        .eq('variant_id', variantData.id)
        .maybeSingle();

      // Fetch prices with history
      const { data: prices } = await supabase
        .from('prices_dk')
        .select('*, price_history(*)')
        .eq('variant_id', variantData.id)
        .order('price_dkk', { ascending: true });

      // Fetch reviews (both variant and model level) — newest first, with created_at as fallback
      const { data: reviews } = await supabase
        .from('review_links')
        .select('*')
        .or(`variant_id.eq.${variantData.id},model_id.eq.${model.id}`)
        .order('publish_date', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });

      // Hent tidligere generationer (kæde) og evt. aktiv efterfølger
      const previousGenerations: PreviousGenLink[] = [];
      let cursor: string | null = (model as any).predecessor_model_id || null;
      const guard = new Set<string>();
      while (cursor && !guard.has(cursor) && previousGenerations.length < 10) {
        guard.add(cursor);
        const { data: prev } = await supabase
          .from('models')
          .select('id, name, slug, model_year, predecessor_model_id, brands!inner(slug), variants(slug, is_active)')
          .eq('id', cursor)
          .maybeSingle();
        if (!prev) break;
        const firstVariant = (prev as any).variants?.find((v: any) => v.is_active) || (prev as any).variants?.[0];
        previousGenerations.push({
          id: prev.id,
          name: prev.name,
          slug: prev.slug,
          model_year: (prev as any).model_year ?? null,
          brand_slug: (prev as any).brands?.slug || brand.slug,
          first_variant_slug: firstVariant?.slug || null,
        });
        cursor = (prev as any).predecessor_model_id || null;
      }

      // Hvis vi er på en tidligere generation, find den aktive efterfølger
      let activeGeneration: PreviousGenLink | null = null;
      if ((model as any).is_previous_generation) {
        const { data: successor } = await supabase
          .from('models')
          .select('id, name, slug, model_year, brands!inner(slug), variants(slug, is_active)')
          .eq('predecessor_model_id', model.id)
          .eq('is_previous_generation', false)
          .maybeSingle();
        if (successor) {
          const firstVariant = (successor as any).variants?.find((v: any) => v.is_active) || (successor as any).variants?.[0];
          activeGeneration = {
            id: successor.id,
            name: successor.name,
            slug: successor.slug,
            model_year: (successor as any).model_year ?? null,
            brand_slug: (successor as any).brands?.slug || brand.slug,
            first_variant_slug: firstVariant?.slug || null,
          };
        }
      }

      // Fetch sibling variants in the same model (for spec fallback)
      const { data: siblingRows } = await supabase
        .from('variants')
        .select('id, name, *, powertrain_specs(*), prices_dk(price_dkk)')
        .eq('model_id', model.id)
        .neq('id', variantData.id)
        .eq('is_active', true);

      const siblings: Sibling[] = (siblingRows || []).map((row: any) => {
        const specsRow = Array.isArray(row.powertrain_specs)
          ? row.powertrain_specs[0]
          : row.powertrain_specs;
        const pricesArr = (row.prices_dk || []) as { price_dkk: number }[];
        const minPrice = pricesArr.length
          ? Math.min(...pricesArr.map((p) => p.price_dkk))
          : null;
        return {
          id: row.id,
          name: row.name,
          price: minPrice,
          variant: row,
          specs: specsRow || null,
        };
      });

      return {
        variant: variantData,
        model: { ...model, brands: undefined } as Model,
        brand,
        specs: specs || null,
        prices: prices || [],
        reviews: reviews || [],
        previousGenerations,
        activeGeneration,
        siblings,
      };
    },
    enabled: !!brandSlug && !!modelSlug && !!variantSlug,
  });

  // Datapunkt-metadata til UBEKRÆFTET-badges og kildeattribution
  const dataPoints = useDataPointMeta([data?.variant?.id, data?.model?.id]);



  if (isLoading) {
    return (
      <PublicLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </PublicLayout>
    );
  }

  if (error || !data) {
    return (
      <PublicLayout>
        <Seo title="Elbil ikke fundet" description="Siden findes ikke længere eller er flyttet." path={window.location.pathname} noindex />
        <div className="container py-12 text-center">
          <Car className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h1 className="text-2xl font-display font-bold mb-2">Elbil ikke fundet</h1>
          <p className="text-muted-foreground mb-6">
            Den elbil, du leder efter, findes ikke eller er blevet fjernet.
          </p>
          <Link
            to="/biler"
            className="inline-flex items-center gap-2 text-primary hover:underline"
          >
            <ChevronRight className="w-4 h-4 rotate-180" />
            Tilbage til elbilsøgning
          </Link>
        </div>
      </PublicLayout>
    );
  }

  const { variant: variantBase, model, brand, prices, reviews, previousGenerations, activeGeneration, siblings } = data;
  const isPreviousGen = (model as any).is_previous_generation === true;
  const specsBase = data.specs as any;
  const isEV = true;
  const isHybrid = false;
  const isUpcoming = variantBase.availability_status === 'upcoming';
  const isDiscontinued = variantBase.availability_status === 'discontinued';
  const lowestPrice = prices.length > 0 ? prices[0] : null;

  // Fill in missing physical/charging fields from the sibling variant whose
  // starting price is closest, and remember which fields we borrowed.
  const { dispVariant, dispSpecs, borrowed } = applySiblingFallback(
    variantBase as any,
    specsBase,
    lowestPrice?.price_dkk ?? null,
    siblings,
  );
  const variant = dispVariant as typeof variantBase;
  const hasAnySpec = !!specsBase || Object.values(dispSpecs).some((v) => v !== null && v !== undefined && v !== '');
  const specs = (hasAnySpec ? dispSpecs : null) as any;
  const borrowedFields: BorrowedField[] = borrowed;

  const selected = isSelected(variant.id);
  const canAdd = selectedCars.length < maxCars;

  const openFlow = (kind: FlowKind, fieldKey?: string) =>
    setSuggestFlow({ kind, fieldKey });

  const carData = {
    variant,
    model,
    brand,
    lowestPrice,
    specs,
  };

  const handleCompareToggle = () => {
    if (selected) {
      removeCar(variant.id);
    } else if (canAdd) {
      addCar(carData);
    }
  };

  return (
    <DataPointProvider value={dataPoints}>
    <PublicLayout>
      <Seo
        title={`${brand.name} ${model.name} ${variant.name}`}
        description={`${brand.name} ${model.name} ${variant.name} – ${isDiscontinued ? 'udgået model, specs og anmeldelser' : `pris${lowestPrice ? ` fra ${formatPrice(lowestPrice.price_dkk)} kr` : ''}, specs og anmeldelser`} på Min Nye Elbil.`}
        path={`/biler/${brand.slug}/${model.slug}/${variant.slug}`}
        image={model.image_url || undefined}
        type="product"
        jsonLd={(() => {
          const productName = `${brand.name} ${model.name} ${variant.name}`;
          const canonicalUrl = `https://minnyeelbil.dk/biler/${brand.slug}/${model.slug}/${variant.slug}`;
          const breadcrumb = {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Forside", item: "https://minnyeelbil.dk/" },
              { "@type": "ListItem", position: 2, name: "Biler", item: "https://minnyeelbil.dk/biler" },
              { "@type": "ListItem", position: 3, name: brand.name, item: `https://minnyeelbil.dk/maerker/${brand.slug}` },
              { "@type": "ListItem", position: 4, name: productName, item: canonicalUrl },
            ],
          };

          if (lowestPrice) {
            // Product schema kræver offers/review/aggregateRating for at være gyldigt til rich results.
            const availability = isDiscontinued
              ? "https://schema.org/Discontinued"
              : isUpcoming
                ? "https://schema.org/PreOrder"
                : "https://schema.org/InStock";
            return [
              {
                "@context": "https://schema.org",
                "@type": "Product",
                name: productName,
                brand: { "@type": "Brand", name: brand.name },
                image: model.image_url || undefined,
                url: canonicalUrl,
                offers: {
                  "@type": "Offer",
                  priceCurrency: "DKK",
                  price: lowestPrice.price_dkk,
                  availability,
                  url: canonicalUrl,
                },
              },
              breadcrumb,
            ];
          }

          // Ingen pris: fald tilbage til Vehicle-schema (kræver ikke offers/review/aggregateRating).
          const additionalProperty: Array<Record<string, unknown>> = [];
          if (specs?.range_wltp_km) {
            additionalProperty.push({
              "@type": "PropertyValue",
              name: "Rækkevidde (WLTP)",
              value: specs.range_wltp_km,
              unitText: "km",
            });
          }
          if (specs?.battery_kwh) {
            additionalProperty.push({
              "@type": "PropertyValue",
              name: "Batterikapacitet",
              value: specs.battery_kwh,
              unitText: "kWh",
            });
          }
          const vehicle: Record<string, unknown> = {
            "@context": "https://schema.org",
            "@type": "Vehicle",
            name: productName,
            brand: { "@type": "Brand", name: brand.name },
            model: `${model.name} ${variant.name}`,
            image: model.image_url || undefined,
            url: canonicalUrl,
            fuelType: variant.powertrain_type === 'EV' ? 'Electric' : variant.powertrain_type,
            bodyType: bodyTypeLabels[model.body_type],
          };

          if (variant.seats) vehicle.seatingCapacity = variant.seats;
          if (variant.doors) vehicle.numberOfDoors = variant.doors;
          if (additionalProperty.length) vehicle.additionalProperty = additionalProperty;
          return [vehicle, breadcrumb];
        })()}

      />
      {/* Breadcrumb */}
      <div className="bg-muted/50 border-b">
        <div className="container py-3">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Forside</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/biler">Biler</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to={`/maerker/${brand.slug}`}>{brand.name}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{model.name} {variant.name}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>

      <div className="container py-5 md:py-8">
        {/* Upcoming Car Banner */}
        {isUpcoming && (
          <Alert className="mb-6 border-amber-500 bg-amber-50 dark:bg-amber-950/20">
            <Clock className="h-4 w-4 text-amber-600" />
            <AlertTitle className="text-amber-800 dark:text-amber-200">Kommende elbil</AlertTitle>
            <AlertDescription className="text-amber-700 dark:text-amber-300">
              Denne elbil er endnu ikke til salg i Danmark.
              {variant.expected_launch_date && (
                <> Forventet lancering: <strong>{format(new Date(variant.expected_launch_date), 'MMMM yyyy', { locale: da })}</strong>.</>
              )}
              {' '}Priser og specifikationer kan ændres.
            </AlertDescription>
          </Alert>
        )}
        {isDiscontinued && (
          <Alert className="mb-6 border-gray-500 bg-gray-50 dark:bg-gray-900/40">
            <AlertTitle className="text-gray-800 dark:text-gray-200">Udgået model</AlertTitle>
            <AlertDescription className="text-gray-700 dark:text-gray-300">
              Denne variant er udgået og kan ikke længere bestilles som ny. Vi viser ikke pris, da den ikke længere er retvisende. Specifikationer vises som historisk reference.
            </AlertDescription>
          </Alert>
        )}

        {/* Tidligere generation banner */}
        {isPreviousGen && (
          <Alert className="mb-6 border-muted bg-muted/40">
            <Car className="h-4 w-4" />
            <AlertTitle>Tidligere modelversion{(model as any).model_year ? ` (${(model as any).model_year})` : ''}</AlertTitle>
            <AlertDescription>
              Du ser en arkiveret årgang af denne model.{' '}
              {activeGeneration && (
                <Link
                  to={`/biler/${activeGeneration.brand_slug}/${activeGeneration.slug}${activeGeneration.first_variant_slug ? `/${activeGeneration.first_variant_slug}` : ''}`}
                  className="underline font-medium"
                >
                  Se den nuværende generation
                </Link>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Tidligere årgange/versioner */}
        {!isPreviousGen && previousGenerations.length > 0 && (
          <div className="mb-6 rounded-lg border bg-muted/30 p-4">
            <h2 className="font-medium mb-2">Tidligere årgange</h2>
            <div className="flex flex-wrap gap-2">
              {previousGenerations.map((p) => (
                <Link
                  key={p.id}
                  to={`/biler/${p.brand_slug}/${p.slug}${p.first_variant_slug ? `/${p.first_variant_slug}` : ''}`}
                  className="inline-flex items-center gap-1 rounded-md border bg-background px-3 py-1.5 text-sm hover:bg-accent transition-colors"
                >
                  {model.name}{p.model_year ? ` · ${p.model_year}` : ''}
                </Link>
              ))}
            </div>
          </div>
        )}


        {/* Header */}
        <div className="grid lg:grid-cols-2 gap-5 lg:gap-8 mb-6 md:mb-10">
          {/* Image — variant-billede har forrang over model-billede */}
          <div className="flex items-center justify-center bg-muted rounded-xl overflow-hidden p-3 sm:p-4 min-h-40 sm:min-h-48">
            {(variant.image_url || model.image_url) ? (
              <img
                src={buildCarImageUrl({ variant, model }, 960) || ''}
                srcSet={buildCarImageSrcSet({ variant, model })}
                sizes="(max-width: 1024px) 100vw, 640px"
                alt={`${brand.name} ${model.name}${variant.image_url ? ` ${variant.name}` : ''}`}
                width={800}
                height={500}
                {...({ fetchpriority: "high" } as any)}
                decoding="async"
                className="max-h-56 sm:max-h-72 w-auto max-w-full object-contain"
              />
            ) : (
              <div className="w-full h-40 sm:h-48 flex items-center justify-center">
                <Car className="w-20 h-20 sm:w-24 sm:h-24 text-muted-foreground/60" />
              </div>
            )}
          </div>

          {/* Title & Quick Info */}
          <div className="space-y-4 lg:space-y-5">
            <div>
              <div className="flex items-center gap-2.5 mb-1.5">
                {brand.logo_url && (
                  <img
                    src={brand.logo_url}
                    alt={brand.name}
                    width={120}
                    height={32}
                    loading="lazy"
                    decoding="async"
                    className="h-6 sm:h-8 w-auto object-contain"
                  />
                )}
                <span className="text-[13px] font-medium uppercase tracking-wide text-muted-foreground">
                  {brand.name}
                </span>
              </div>
              <h1 className="font-display text-[26px] leading-[1.15] font-bold sm:text-3xl md:text-4xl">
                {model.name}{' '}
                <span className="text-primary">{variant.name}</span>
                {variant.model_year && (
                  <span className="text-muted-foreground font-normal text-xl sm:text-2xl md:text-3xl"> · {variant.model_year}</span>
                )}
              </h1>
              {(model as any).year_changelog && (
                <p className="text-[13px] text-muted-foreground italic mt-1 leading-snug">
                  Nyt i {(model as any).model_year ?? 'denne årgang'}: {(model as any).year_changelog}
                </p>
              )}
            </div>

            {/* Egenskaber */}
            <div className="flex flex-wrap items-center gap-1.5">
              {isUpcoming && (
                <Badge variant="upcoming" className="gap-1">
                  <Clock className="w-3 h-3" />
                  Kommer snart
                </Badge>
              )}
              <Badge variant={getPowertrainBadgeVariant(variant.powertrain_type)}>
                {powertrainLabels[variant.powertrain_type]}
              </Badge>
              <Badge variant="outline">{bodyTypeLabels[model.body_type]}</Badge>
              <Badge variant="outline">{drivetrainLabels[variant.drivetrain]}</Badge>
              <Badge variant="outline">{transmissionLabels[variant.transmission]}</Badge>

              {/* Andre varianter af modellen — synligt i toppen */}
              {siblings.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    document.getElementById('varianter')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="inline-flex h-9 items-center gap-1.5 rounded-md border border-primary/40 bg-primary/5 px-2.5 text-[13px] font-medium text-primary transition-colors hover:bg-primary/10"
                >
                  <Layers className="w-3.5 h-3.5" />
                  +{siblings.length} {siblings.length === 1 ? 'anden variant' : 'andre varianter'}
                </button>
              )}
            </div>

            {/* Price Highlight — prisen er den visuelle hovedperson */}
            {lowestPrice && !isDiscontinued && (
              <div className="flex items-end justify-between gap-3 rounded-xl border border-primary/20 bg-primary/[0.06] px-4 py-3">
                <div className="min-w-0">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    {priceTypeLabels[lowestPrice.price_type]}
                  </p>
                  <p className="mt-1 font-display text-[30px] font-bold leading-none tracking-tight text-primary sm:text-3xl">
                    {formatPrice(lowestPrice.price_dkk)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => openFlow('price')}
                  className="inline-flex min-h-[44px] shrink-0 items-center gap-1 text-[13px] text-muted-foreground underline-offset-4 transition-colors hover:text-accent hover:underline"
                >
                  <TrendingUp className="h-3.5 w-3.5" />
                  Opdater pris
                </button>
              </div>
            )}

            {/* Prisspænd for modellen */}
            {(() => {
              const modelPrices = [
                ...siblings.map((s) => s.price),
                lowestPrice?.price_dkk ?? null,
              ].filter((p): p is number => typeof p === 'number' && p > 0);
              if (siblings.length === 0 || modelPrices.length === 0) return null;
              return (
                <p className="-mt-2 text-[13px] text-muted-foreground">
                  Modellen fås fra <strong className="text-foreground">{formatPrice(Math.min(...modelPrices))}</strong>
                </p>
              );
            })()}

            {/* Primære handlinger */}
            <div className="flex flex-wrap items-center gap-2">
              <FavoriteButton variantId={variant.id} variantStyle="full" className="min-h-[44px] text-sm" />
              <PriceAlertButton
                variantId={variant.id}
                currentPrice={lowestPrice?.price_dkk ?? null}
              />
              <Button
                variant={selected ? 'default' : 'outline'}
                size="sm"
                onClick={handleCompareToggle}
                disabled={!selected && !canAdd}
                className="min-h-[44px]"
              >
                {selected ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Tilføjet
                  </>
                ) : (
                  <>
                    <GitCompareArrows className="w-4 h-4 mr-2" />
                    Sammenlign
                  </>
                )}
              </Button>
            </div>

            {/* Sekundære handlinger — samlet og diskrete */}
            <div className="border-t border-border pt-3">
              <button
                type="button"
                onClick={() => setMoreActionsOpen((o) => !o)}
                aria-expanded={moreActionsOpen}
                className="inline-flex min-h-[44px] items-center gap-1 text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground sm:hidden"
              >
                Flere handlinger
                <ChevronRight
                  className={`h-4 w-4 transition-transform ${moreActionsOpen ? 'rotate-90' : ''}`}
                  aria-hidden
                />
              </button>
              <div
                className={`${moreActionsOpen ? 'flex' : 'hidden'} flex-wrap items-center gap-x-2 gap-y-1.5 sm:flex`}
              >
                <ShareButton
                  context="car"
                  title={`${brand.name} ${model.name} ${variant.name}`}
                  size="sm"
                />
                <ReportErrorButton
                  brandName={brand.name}
                  modelName={model.name}
                  variantName={variant.name}
                />
                <ContributeButton onClick={() => openFlow('tech')} />
                <SuggestImageDialog
                  variantId={variant.id}
                  variantLabel={`${brand.name} ${model.name} ${variant.name}`}
                  brandSlug={brand.slug}
                  modelSlug={model.slug}
                  variantSlug={variant.slug}
                />
              </div>
            </div>

            {/* Key Specs */}
            <div className="grid grid-cols-3 gap-2 border-t border-border pt-3">
              {specs?.power_hp && (
                <div className="min-w-0">
                  <p className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                    <Zap className="h-3 w-3 shrink-0 text-accent" aria-hidden />
                    <span className="truncate">Ydelse</span>
                  </p>
                  <p className="mt-0.5 font-display text-lg font-bold leading-tight">{specs.power_hp} hk</p>
                </div>
              )}
              {(isEV || isHybrid) && specs?.range_wltp_km && (
                <div className="min-w-0">
                  <p className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                    <MapPin className="h-3 w-3 shrink-0 text-accent" aria-hidden />
                    <span className="truncate">Rækkevidde</span>
                  </p>
                  <p className="mt-0.5 font-display text-lg font-bold leading-tight">{formatNumber(specs.range_wltp_km)} km</p>
                </div>
              )}
              {specs?.acceleration_0_100 && (
                <div className="min-w-0">
                  <p className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                    <Timer className="h-3 w-3 shrink-0 text-accent" aria-hidden />
                    <span className="truncate">0-100</span>
                  </p>
                  <p className="mt-0.5 font-display text-lg font-bold leading-tight">{specs.acceleration_0_100} sek</p>
                </div>
              )}
            </div>
          </div>
        </div>


        {/* Nøgletal — beslutningstallene samlet øverst */}
        <KeyFactsBar
          price={lowestPrice?.price_dkk ?? (variant as any).expected_price_dkk ?? null}
          rangeKm={specs?.range_wltp_km ?? null}
          batteryKwh={specs?.battery_kwh ?? null}
          dcKw={specs?.charging_dc_kw ?? null}
          dc1080Min={specs?.dc_charge_10_80_min ?? null}
          consumptionWhKm={specs?.consumption_wh_km ?? null}
        />

        {/* Beregnere: rækkevidde, opladning og driftsøkonomi */}
        <CarCalculators
          rangeKm={specs?.range_wltp_km ?? null}
          batteryKwh={specs?.battery_kwh ?? null}
          dcKw={specs?.charging_dc_kw ?? null}
          dc1080Min={specs?.dc_charge_10_80_min ?? null}
          acKw={specs?.charging_ac_kw ?? null}
          consumptionWhKm={specs?.consumption_wh_km ?? null}
          annualTaxDkk={
            (variant as any).green_owner_tax_yearly_dkk ??
            (variant as any).owner_tax_yearly_dkk ??
            null
          }
        />

        {/* Pris, afgift og ladeomkostning ud fra seneste elpris */}
        <OwnershipCostBlock
          priceDkk={lowestPrice?.price_dkk ?? null}
          annualTaxDkk={
            (variant as any).green_owner_tax_yearly_dkk ??
            (variant as any).owner_tax_yearly_dkk ??
            null
          }
          consumptionWhKm={specs?.consumption_wh_km ?? null}
          batteryKwh={specs?.battery_kwh ?? null}
        />

        {/* Kildeattribution i prosa */}
        <SourceAttribution
          dataPoints={dataPoints}
          brandName={brand.name}
          modelName={model.name}
          variantName={variant.name}
          rangeWltpKm={specs?.range_wltp_km ?? null}
          batteryKwh={specs?.battery_kwh ?? null}
          consumptionWhKm={specs?.consumption_wh_km ?? null}
          chargingDcKw={specs?.charging_dc_kw ?? null}
          priceDkk={lowestPrice?.price_dkk ?? null}
        />






        {/* Missing data — community contributions */}
        <div className="mb-6 md:mb-8">
          <MissingFieldsBox
            variant={variant}
            specs={specs}
            hasPrice={!!lowestPrice || isDiscontinued}
            onChipClick={(fieldKey) => {
              // Pris-felter åbner pris-flowet, ellers åbner mangler-flowet
              if (fieldKey === 'price_dkk') openFlow('price', 'price_dkk');
              else openFlow('missing', fieldKey);
            }}
          />
        </div>

        {/* Affiliate CTA blok – tydeligt adskilt fra bildata og mærket som annonce */}
        <div className="my-6 border-y border-border bg-muted/30 -mx-4 px-4 py-5 sm:mx-0 sm:rounded-xl sm:border sm:px-5 md:my-8">
          <CarDetailAffiliateBlock />
        </div>

        {/* Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6 lg:space-y-8 min-w-0">
            {/* Reviews */}
            <Card>
              <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-x-3 gap-y-2 p-4 pb-3 sm:p-5 sm:pb-4">
                <CardTitle className="flex items-center gap-2 font-display text-[19px] sm:text-xl">
                  <FileText className="w-[18px] h-[18px] text-primary" />
                  Anmeldelser og tests
                </CardTitle>
                <SuggestReviewButton
                  brandId={brand.id}
                  modelId={model.id}
                  variantId={variant.id}
                  brandName={brand.name}
                  modelName={model.name}
                  variantName={variant.name}
                />
              </CardHeader>
              <CardContent className="p-4 pt-0 sm:p-5 sm:pt-0">
                {reviews.length > 0 ? (
                  <div className="space-y-2.5">
                    <p className="text-[11px] leading-snug text-muted-foreground mb-1">
                      Anmeldelser fra eksterne medier — åbnes i en ny fane, så du beholder denne side.
                    </p>
                    {reviews.map((review) => {
                      const dateStr = review.publish_date || review.created_at;
                      const isNew = dateStr
                        ? (Date.now() - new Date(dateStr).getTime()) < 30 * 24 * 60 * 60 * 1000
                        : false;
                      const handleReviewClick = () => {
                        // Fire-and-forget click tracking; never block navigation
                        trackEvent('outbound_click', {
                          link_url: review.url,
                          link_name: review.publisher,
                          page_source: 'car_detail',
                        });
                        supabase.from('external_link_clicks').insert({
                          url: review.url,
                          page_source: 'car_detail',
                          link_name: review.publisher,
                          referrer: typeof window !== 'undefined' ? window.location.pathname : null,
                          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
                        }).then(() => {}, () => {});
                        toast.info('Anmeldelsen åbner i en ny fane', {
                          description: 'Denne side venter på dig her.',
                          duration: 4000,
                        });
                      };
                      return (
                        <div
                          key={review.id}
                          className="group block rounded-lg border border-border border-l-[3px] border-l-accent bg-card px-3 py-2.5 transition-colors hover:border-primary/50 hover:bg-accent/5"
                        >
                          <div className="flex items-start gap-2.5">
                            <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-primary/10">
                              {review.format === 'Video' ? (
                                <Video className="w-3.5 h-3.5 text-primary" />
                              ) : (
                                <FileText className="w-3.5 h-3.5 text-primary" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="mb-1 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                                <span>{reviewFormatLabels[review.format]}</span>
                                <span aria-hidden>·</span>
                                <span>{reviewLanguageLabels[review.language]}</span>
                                {isNew && (
                                  <Badge variant="default" className="h-4 px-1.5 text-[10px] leading-none">Ny</Badge>
                                )}
                              </div>
                              <a
                                href={review.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={handleReviewClick}
                                aria-label={`Læs anmeldelse hos ${review.publisher} (åbner i ny fane)`}
                                className="inline-flex items-center gap-1.5 hover:text-primary transition-colors"
                              >
                                <h4 className="text-[15px] font-semibold leading-snug line-clamp-2">{review.title}</h4>
                                <ExternalLink className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                              </a>
                              <p className="text-[13px] text-muted-foreground mt-0.5">
                                <Link
                                  to={`/anmeldelser/udgiver/${publisherToSlug(review.publisher)}`}
                                  className="font-bold italic text-foreground hover:text-primary hover:underline"
                                >
                                  {review.publisher}
                                </Link>
                                {review.publish_date && (
                                  <> · {formatDate(review.publish_date)}</>
                                )}
                              </p>
                              {review.summary && (
                                <p className="text-[13px] leading-snug text-muted-foreground mt-1.5 line-clamp-2">
                                  {review.summary}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {/* "Mere på Bilbase" CTA */}
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-secondary/40 px-3 py-2.5">
                      <span className="text-[13px] text-muted-foreground">
                        Hold dig på Min Nye Elbil — udforsk mere om denne bil:
                      </span>
                      <div className="flex flex-wrap gap-2">
                        <Link to="/anmeldelser">
                          <Button variant="outline" size="sm">Alle anmeldelser</Button>
                        </Link>
                        <a href="#similar-cars">
                          <Button variant="outline" size="sm">Lignende elbiler</Button>
                        </a>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6 text-muted-foreground">
                    <p className="mb-2">Ingen anmeldelser endnu</p>
                    <p className="text-sm">Kender du en anmeldelse af denne elbil?</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Estimated-fields notice */}
            {borrowedFields.length > 0 && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Nogle felter er estimeret</AlertTitle>
                <AlertDescription>
                  Vi mangler bekræftede tal for denne variant på følgende felter og viser derfor værdier
                  fra den variant, der ligger tættest i pris. Tallene kan afvige fra de officielle.
                  <ul className="mt-2 list-disc pl-5 space-y-0.5 text-sm">
                    {borrowedFields.map((b) => (
                      <li key={b.field}>
                        <span className="font-medium">{b.label}</span> — fra {b.fromName}
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Powertrain Specs */}
            {specs && (
              <Card>
                <CardHeader className="p-4 pb-3 sm:p-5 sm:pb-4">
                  <div className="flex flex-row flex-wrap items-center justify-between gap-x-3 gap-y-2">
                    <CardTitle className="flex items-center gap-2 font-display text-[19px] sm:text-xl">
                      <Gauge className="w-[18px] h-[18px] text-primary" />
                      Tekniske specifikationer
                    </CardTitle>
                    <ContributeButton onClick={() => openFlow('tech')} />
                  </div>
                </CardHeader>
                <CardContent className="p-4 pt-0 sm:p-5 sm:pt-0">
                  <div className="grid sm:grid-cols-2 sm:gap-x-8">
                    {/* Engine/Motor Section */}
                    <div className="pt-2">
                      <h4 className="pb-0.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Motor og ydelse
                      </h4>
                      <div>
                        {specs.engine && (
                          <SpecRow label="Motor" value={specs.engine} />
                        )}
                        {specs.power_hp && (
                          <SpecRow label="Ydelse" value={`${specs.power_hp} hk`} />
                        )}
                        {specs.torque_nm && (
                          <SpecRow label="Drejningsmoment" value={`${formatNumber(specs.torque_nm)} Nm`} />
                        )}
                        {specs.acceleration_0_100 && (
                          <SpecRow label="0-100 km/t" value={`${specs.acceleration_0_100} sek`} />
                        )}
                        {specs.top_speed_kmh && (
                          <SpecRow label="Topfart" value={`${specs.top_speed_kmh} km/t`} />
                        )}
                      </div>
                    </div>

                    {/* Battery/Fuel Section */}
                    <div className="pt-2">
                      <h4 className="pb-0.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Batteri og opladning
                      </h4>
                      <div>
                        {(isEV || isHybrid) && specs.battery_kwh && (
                          <SpecRow label="Batteri (netto)" value={`${specs.battery_kwh} kWh`} field="battery_kwh" />
                        )}
                        {(isEV || isHybrid) && specs.batteri_brutto_kwh && (
                          <SpecRow label="Batteri (brutto)" value={`${specs.batteri_brutto_kwh} kWh`} />
                        )}
                        {(isEV || isHybrid) && specs.range_wltp_km && (
                          <SpecRow label="Rækkevidde (WLTP)" value={`${formatNumber(specs.range_wltp_km)} km`} field="range_wltp_km" />
                        )}
                        {(isEV || isHybrid) && specs.realistisk_vinterraekkevidde_km && (
                          <SpecRow label="Vinterrækkevidde" value={`${formatNumber(specs.realistisk_vinterraekkevidde_km)} km`} />
                        )}
                        {(isEV || isHybrid) && specs.motorvejsraekkevidde_km && (
                          <SpecRow label="Motorvejsrækkevidde" value={`${formatNumber(specs.motorvejsraekkevidde_km)} km`} />
                        )}
                        {(isEV || isHybrid) && specs.byraekkevidde_km && (
                          <SpecRow label="Byrækkevidde" value={`${formatNumber(specs.byraekkevidde_km)} km`} />
                        )}
                        {(isEV || isHybrid) && specs.consumption_wh_km && (
                          <SpecRow label="Elforbrug" value={`${specs.consumption_wh_km} Wh/km`} field="consumption_wh_km" />
                        )}
                        {(isEV || isHybrid) && specs.charging_ac_kw && (
                          <SpecRow label="AC-opladning" value={`${specs.charging_ac_kw} kW${specs.ac_faser ? ` (${specs.ac_faser}-faset)` : ''}`} field="charging_ac_kw" />
                        )}
                        {(isEV || isHybrid) && specs.charging_dc_kw && (
                          <SpecRow label="DC-hurtigladning" value={`${specs.charging_dc_kw} kW`} field="charging_dc_kw" />
                        )}
                        {(isEV || isHybrid) && specs.dc_charge_10_80_min && (
                          <SpecRow label="Ladetid 10-80%" value={`${specs.dc_charge_10_80_min} min`} />
                        )}
                        {(isEV || isHybrid) && specs.stiktype && (
                          <SpecRow label="Stiktype" value={specs.stiktype} />
                        )}
                        {(isEV || isHybrid) && specs.ladestik_placering && (
                          <SpecRow label="Ladestik placering" value={specs.ladestik_placering} />
                        )}
                        {(isEV || isHybrid) && specs.batterikemi && (
                          <SpecRow label="Batterikemi" value={specs.batterikemi} />
                        )}
                        {!isEV && specs.l_per_100km && (
                          <SpecRow label="Brændstofforbrug" value={`${specs.l_per_100km} L/100 km`} />
                        )}
                        {!isEV && specs.km_pr_l && (
                          <SpecRow label="Km pr. liter" value={`${specs.km_pr_l} km/l`} />
                        )}
                        {!isEV && specs.co2_g_km && (
                          <SpecRow label="CO₂-udledning" value={`${specs.co2_g_km} g/km`} />
                        )}
                        {!isEV && specs.fuel_tank_l && (
                          <SpecRow label="Tankstørrelse" value={`${specs.fuel_tank_l} L`} />
                        )}
                        {!isEV && specs.estimeret_raekkevidde_km && (
                          <SpecRow label="Estimeret rækkevidde" value={`${formatNumber(specs.estimeret_raekkevidde_km)} km`} />
                        )}
                        {!isEV && specs.motorstoerrelse_cc && (
                          <SpecRow label="Motorstørrelse" value={`${formatNumber(specs.motorstoerrelse_cc)} cc`} />
                        )}
                        {!isEV && specs.cylindre && (
                          <SpecRow label="Cylindre" value={`${specs.cylindre}`} />
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Vehicle Specs */}
            <Card>
              <CardHeader className="p-4 pb-3 sm:p-5 sm:pb-4">
                <div className="flex flex-row flex-wrap items-center justify-between gap-x-3 gap-y-2">
                  <CardTitle className="flex items-center gap-2 font-display text-[19px] sm:text-xl">
                    <Car className="w-[18px] h-[18px] text-primary" />
                    Køretøjsdata
                  </CardTitle>
                  <ContributeButton onClick={() => openFlow('vehicle')} />
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0 sm:p-5 sm:pt-0">
                <div className="grid sm:grid-cols-2 sm:gap-x-8">
                  <div className="pt-2">
                    <SpecRow label="Karosseri" value={bodyTypeLabels[model.body_type]} />
                    <SpecRow label="Drivlinje" value={drivetrainLabels[variant.drivetrain]} />
                    <SpecRow label="Gearkasse" value={transmissionLabels[variant.transmission]} />
                    {variant.seats && (
                      <div>
                        <SpecRow 
                          label="Siddepladser" 
                          value={variant.max_seats && variant.max_seats > variant.seats 
                            ? `${variant.seats} – ${variant.max_seats}${variant.extra_seats_optional ? '*' : ''}` 
                            : variant.seats.toString()} 
                          icon={<Users className="w-4 h-4" />} 
                        />
                        {variant.max_seats && variant.max_seats > variant.seats && (
                          <p className="pb-2 text-[11px] leading-snug italic text-muted-foreground">
                            {variant.extra_seats_optional
                              ? `* ${variant.max_seats - variant.seats} ekstra sæde${variant.max_seats - variant.seats > 1 ? 'r' : ''} er tilvalg (ikke standard)`
                              : `Op til ${variant.max_seats} sæder tilgængelig`}
                          </p>
                        )}
                      </div>
                    )}
                    {variant.doors && (
                      <SpecRow label="Døre" value={variant.doors.toString()} icon={<DoorOpen className="w-4 h-4" />} />
                    )}
                    {variant.isofix_count != null ? (
                      <SpecRow
                        label="Isofix"
                        value={`${variant.isofix_count} stk.`}
                        icon={<Baby className="w-4 h-4" />}
                      />
                    ) : (
                      <SpecRow
                        label="Isofix"
                        value="Ikke oplyst"
                        icon={<Baby className="w-4 h-4" />}
                      />
                    )}
                    <SpecRow
                      label="3 Isofix på 2. sæderække"
                      value={variant.isofix_three_in_second_row ? 'Ja' : 'Nej'}
                      icon={<Baby className="w-4 h-4" />}
                    />
                  </div>
                  <div className="pt-2">
                    {variant.trunk_l && (
                      <SpecRow label="Bagagerum" value={`${formatNumber(variant.trunk_l)} L`} icon={<Package className="w-4 h-4" />} />
                    )}
                    {variant.curb_weight_kg && (
                      <SpecRow label="Egenvægt" value={`${formatNumber(variant.curb_weight_kg)} kg`} icon={<Weight className="w-4 h-4" />} />
                    )}
                    {variant.tow_kg_braked && (
                      <SpecRow label="Trækvægt m/bremse" value={`${formatNumber(variant.tow_kg_braked)} kg`} field="tow_kg_braked" />
                    )}
                    {variant.tow_kg_unbraked && (
                      <SpecRow label="Trækvægt u/bremse" value={`${formatNumber(variant.tow_kg_unbraked)} kg`} field="tow_kg_unbraked" />
                    )}
                    {variant.tow_bar_standard && (
                      <SpecRow label="Trækkrog" value="Standard" />
                    )}
                  </div>
                </div>

                {/* Dimensions */}
                {(variant.length_m || variant.width_m || variant.height_m || variant.wheelbase_m) && (
                  <>
                    <Separator className="my-4" />
                    <div>
                      <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Dimensioner
                      </h4>
                      <div className="grid sm:grid-cols-2 sm:gap-x-8">
                        {variant.length_m && (
                          <SpecRow label="Længde" value={`${variant.length_m} m`} />
                        )}
                        {variant.width_m && (
                          <SpecRow label="Bredde (uden spejle)" value={`${variant.width_m} m`} field="width_m" />
                        )}
                        {variant.height_m && (
                          <SpecRow label="Højde" value={`${variant.height_m} m`} />
                        )}
                        {variant.wheelbase_m && (
                          <SpecRow label="Akselafstand" value={`${variant.wheelbase_m} m`} />
                        )}
                        {variant.turning_diameter_m && (
                          <SpecRow label="Vendediameter" value={`${variant.turning_diameter_m} m`} />
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Warranty */}
                {(variant.warranty_years || variant.warranty_km || variant.battery_warranty_years) && (
                  <>
                    <Separator className="my-4" />
                    <div>
                      <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Garanti
                      </h4>
                      <div className="grid sm:grid-cols-2 sm:gap-x-8">
                        {variant.warranty_years && (
                          <SpecRow label="Fabriksgaranti" value={`${variant.warranty_years} år${variant.warranty_km ? ` / ${formatNumber(variant.warranty_km)} km` : ''}`} />
                        )}
                        {variant.battery_warranty_years && (
                          <SpecRow label="Batterigaranti" value={`${variant.battery_warranty_years} år${variant.battery_warranty_km ? ` / ${formatNumber(variant.battery_warranty_km)} km` : ''}`} />
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Extra features */}
                {(variant.heat_pump_standard || variant.euro_ncap || variant.frunk_liter || specs?.v2l || specs?.v2h || specs?.batteriforvarmning || specs?.ota_opdateringer || specs?.ruteplanlaegger_med_ladestop || specs?.aktiv_batteritemperering || specs?.udligningsafgift) && (
                  <>
                    <Separator className="my-4" />
                    <div>
                      <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Ekstra udstyr & funktioner
                      </h4>
                      <div className="grid sm:grid-cols-2 sm:gap-x-8">
                        {variant.euro_ncap && (
                          <SpecRow label="Euro NCAP" value={variant.euro_ncap} />
                        )}
                        {variant.heat_pump_standard && (
                          <SpecRow label="Varmepumpe" value="Standard" />
                        )}
                        {variant.frunk_liter && (
                          <SpecRow label="Frunk" value={`${variant.frunk_liter} L`} />
                        )}
                        {specs?.batteriforvarmning && (
                          <SpecRow label="Batteriforvarmning" value="Ja" />
                        )}
                        {specs?.aktiv_batteritemperering && (
                          <SpecRow label="Aktiv batteritemperering" value="Ja" />
                        )}
                        {specs?.v2l && (
                          <SpecRow label="V2L (Vehicle-to-Load)" value="Ja" />
                        )}
                        {specs?.v2h && (
                          <SpecRow label="V2H (Vehicle-to-Home)" value="Ja" />
                        )}
                        {specs?.ota_opdateringer && (
                          <SpecRow label="OTA-opdateringer" value="Ja" />
                        )}
                        {specs?.ruteplanlaegger_med_ladestop && (
                          <SpecRow label="Ruteplanlægger med ladestop" value="Ja" />
                        )}
                        {variant.is_commercial && (
                          <SpecRow label="Elbiltype" value="Erhvervs-elbil" />
                        )}
                        {variant.green_owner_tax_yearly_dkk && (
                          <SpecRow label="CO₂-ejerafgift" value={`${formatNumber(variant.green_owner_tax_yearly_dkk)} kr./år`} />
                        )}
                        {specs?.udligningsafgift && (
                          <SpecRow label="Udligningsafgift" value={`${formatNumber(specs.udligningsafgift)} kr.`} />
                        )}
                      </div>
                    </div>
                  </>
                )}
                {variant.notes && (
                  <>
                    <Separator className="my-4" />
                    <div>
                      <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Noter
                      </h4>
                      <p className="text-[13px] leading-relaxed text-muted-foreground sm:text-sm">{variant.notes}</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Standardudstyr - midlertidigt skjult, ikke klar endnu */}
            {/* <EquipmentBox
              variant={variant}
              specs={specs}
              onChipClick={(fieldKey) => openFlow('equipment', fieldKey)}
              onContribute={() => openFlow('equipment')}
            /> */}



            {/* Dealer Links */}
            <DealerLinksSection modelId={model.id} modelName={model.name} brandName={brand.name} />
          </div>

          {/* Sidebar */}
          <div className="space-y-5 lg:space-y-6 min-w-0">

            {/* User vote */}
            <VariantVoteBox variantId={variant.id} />

            {/* Brugerstemmer: grunde til at vælge */}
            <VariantReasonsVote variantId={variant.id} />

            {/* Brugerstemmer: nærmeste konkurrent */}
            <VariantCompetitorVote
              variantId={variant.id}
              currentModel={model}
              currentPrice={lowestPrice?.price_dkk || null}
            />


            {/* Other Variants of Same Model */}
            <div id="varianter" className="scroll-mt-24">
              <ModelVariants
                currentVariantId={variant.id}
                currentVariant={variant}
                model={model}
                brand={brand}
                currentSpecs={specs}
                currentPrice={lowestPrice?.price_dkk || null}
              />
            </div>

            {/* Læs om denne bil — vores egne artikler */}
            <RelatedArticles variantId={variant.id} modelId={model.id} />

            {/* Community — variant-specific */}
            <VariantCommunitySection
              variantId={variant.id}
              modelId={model.id}
              brandId={brand.id}
              brandSlug={brand.slug}
              modelSlug={model.slug}
              modelName={model.name}
              brandName={brand.name}
            />

            <FollowUsCard placement="car-detail" />




            {/* Community for this model */}
            <ModelCommunityCard
              modelId={model.id}
              brandSlug={brand.slug}
              modelSlug={model.slug}
              modelName={model.name}
              brandName={brand.name}
            />

            {/* All Prices with History */}
            {!isDiscontinued && <PriceHistorySection prices={prices} />}


            {/* Brand Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Om {brand.name}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {brand.logo_url && (
                  <div className="flex justify-center py-4">
                    <img
                      src={brand.logo_url}
                      alt={brand.name}
                      width={120}
                      height={64}
                      loading="lazy"
                      decoding="async"
                      className="h-16 w-auto object-contain"
                    />
                  </div>
                )}
                {brand.country && (
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium">Oprindelse:</span> {brand.country}
                  </p>
                )}
                {brand.parent_company && (
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium">Moderselskab:</span> {brand.parent_company}
                  </p>
                )}
                {brand.description && (
                  <p className="text-sm text-muted-foreground">{brand.description}</p>
                )}
                {brand.website_url && (
                  <a
                    href={brand.website_url}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border hover:border-primary/50 hover:bg-accent/5 text-sm font-medium text-primary transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Besøg {brand.name}s officielle side
                  </a>
                )}
                <p className="text-xs text-muted-foreground pt-1">
                  Tjek altid endelige specs og priser hos producenten.
                </p>
              </CardContent>
              </Card>
            </div>
          </div>


          {/* Similar Cars Section */}
          <div id="similar-cars" className="scroll-mt-20 mt-6 md:mt-8">
            <SimilarCars
              currentVariant={variant}
              currentModel={model}
              currentSpecs={specs}
              currentPrice={lowestPrice?.price_dkk || null}
            />
        </div>

        <DataTrustFooter
          variantId={variant.id}
          modelId={model.id}
          updatedAt={(variant as any).updated_at ?? null}
        />

        </div>

        {/* Multi-variant forslag-dialog (delt af alle bidrag-flows) */}
        {suggestFlow && (
          <MultiVariantSuggestDialog
            open={!!suggestFlow}
            onOpenChange={(o) => { if (!o) setSuggestFlow(null); }}
            modelId={model.id}
            brandName={brand.name}
            modelName={model.name}
            flowKind={suggestFlow.kind}
            fields={getFlowFields(suggestFlow.kind, variant.powertrain_type)}
            focusedVariantId={variant.id}
            focusedFieldKey={suggestFlow.fieldKey ?? null}
          />
        )}
        <RelatedNewsRow
          brandId={brand.id}
          modelId={model.id}
          variantId={variant.id}
          heading={`Nyheder om ${brand.name} ${model.name}`}
        />
      </PublicLayout>
    </DataPointProvider>
    );
}

// Helper component for spec rows
function SpecRow({
  label,
  value,
  icon,
  field,
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
  /** Feltnavn i databasen — bruges til at vise UBEKRÆFTET-badge */
  field?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border/60 py-2 last:border-0">
      <span className="flex min-w-0 max-w-[58%] items-start gap-1.5 text-[13px] leading-snug text-muted-foreground sm:text-sm">
        {icon}
        <span className="break-words">{label}</span>
      </span>
      <span className="min-w-0 flex-1 break-words text-right text-[13px] font-semibold leading-snug text-foreground sm:text-sm">
        {value}
        <UnverifiedBadge field={field} />
      </span>
    </div>
  );
}
